<template>
    <div id="VisitorMediaAndDocuments">
        <div v-if="loading" class="loading-container">
            <div class="loading-spinner"></div>
        </div>
        <div v-else class="media-container">
            <!-- Header Section -->
            <div class="header-section">
                <div class="tab-menu">
                    <button
                        v-for="(tab, index) in tabs"
                        :key="index"
                        @click="handleTabClick(tab.id)"
                        :class="['tab-btn', { active: activeTab === tab.id }]">
                        <i :class="tab.icon"></i>
                        <span>{{ tab.name }}</span>
                    </button>
                </div>

                <!-- Selection Info Badge -->
                <div v-if="selectedImages.length > 0 || selectedFiles.length > 0" class="selection-info">
                    <span class="selection-count">
                        {{ activeTab === "images" ? selectedImages.length : selectedFiles.length }}
                        {{ $t("auto.selected") }}
                    </span>
                </div>
            </div>

            <!-- Images Tab Content -->
            <div v-if="activeTab === 'images'" class="content-wrapper">
                <div v-if="images.length === 0" class="empty-state">
                    <div class="empty-icon">
                        <i class="fas fa-images"></i>
                    </div>
                    <p class="empty-text">{{ $t("message.noMediaDocuments") }}</p>
                </div>

                <div v-else>
                    <!-- Download Button -->
                    <transition name="slide-down">
                        <div v-if="selectedImages.length > 0" class="action-bar">
                            <button @click="downloadSelectedImages" class="download-button" :disabled="isDownloading">
                                <i class="fas fa-download"></i>
                                <span>{{ isDownloading ? downloadProgress : $t("message.download") }}</span>
                            </button>
                        </div>
                    </transition>

                    <!-- Images Grid -->
                    <div class="image-grid">
                        <div
                            v-for="(image, index) in images"
                            :key="index"
                            :class="['image-card', { selected: selectedImages.includes(image.url) }]"
                            @click="openVisitorGallery(images, index)"
                        >
                            <!-- Custom Checkbox -->
                            <div class="checkbox-wrapper" @click.stop>
                                <input
                                    type="checkbox"
                                    :id="'img-check-' + index"
                                    @change="toggleImageSelection(image.url)"
                                    :disabled="(isSelectionLimitReached && !selectedImages.includes(image.url)) || isDownloading"
                                    :checked="selectedImages.includes(image.url)"
                                    class="custom-checkbox"
                                />
                                <label :for="'img-check-' + index" class="checkbox-label"></label>
                            </div>

                            <img
                                :src="image.url"
                                :alt="image.name"
                                class="image"
                                loading="lazy"
                                decoding="async"
                                @error="onImageLoadError"
                            />

                            <div class="image-overlay">
                                <div class="overlay-content">
                                    <i class="fas fa-images zoom-icon"></i>
                                    <span class="image-name">{{ truncateFileName(image.name, 20) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Files Tab Content -->
            <div v-if="activeTab === 'files'" class="content-wrapper">
                <div v-if="files.length === 0" class="empty-state">
                    <div class="empty-icon">
                        <i class="fas fa-folder-open"></i>
                    </div>
                    <p class="empty-text">{{ $t("message.noMediaDocuments") }}</p>
                </div>

                <div v-else>
                    <!-- Download Button -->
                    <transition name="slide-down">
                        <div v-if="selectedFiles.length > 0" class="action-bar">
                            <button @click="downloadSelectedFiles" class="download-button" :disabled="isDownloading">
                                <i class="fas fa-download"></i>
                                <span>{{ isDownloading ? downloadProgress : $t("message.download") }}</span>
                            </button>
                        </div>
                    </transition>

                    <!-- Files List -->
                    <div class="file-list">
                        <div
                            v-for="(file, index) in files"
                            :key="index"
                            :class="['file-card', { selected: selectedFiles.includes(file.url) }]"
                            @click="openVisitorGallery(files, index)"
                        >
                            <!-- Custom Checkbox -->
                            <div class="checkbox-wrapper" @click.stop>
                                <input
                                    type="checkbox"
                                    :id="'file-check-' + index"
                                    @change="toggleFileSelection(file.url)"
                                    :disabled="(isSelectionLimitReached && !selectedFiles.includes(file.url)) || isDownloading"
                                    :checked="selectedFiles.includes(file.url)"
                                    class="custom-checkbox"
                                />
                                <label :for="'file-check-' + index" class="checkbox-label"></label>
                            </div>

                            <div class="file-icon-wrapper">
                                <i class="fas fa-file-alt file-icon"></i>
                            </div>

                            <div class="file-info">
                                <p class="file-name" :title="file.name">{{ file.name }}</p>
                                <p class="file-size">{{ file.size }}</p>
                            </div>

                            <button type="button" class="file-action-btn" @click.stop="openFile(file.url)">
                                <i class="fas fa-external-link-alt"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <ChatMediaGallery
            :visible.sync="visitorGalleryVisible"
            :items="visitorGalleryItems"
            :initial-index="visitorGalleryIndex"
            :show-go-to-message="visitorGalleryShowGoToMessage"
            @go-to-message="handleVisitorGalleryGoToMessage"
        />
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { downloadUrlsAsZip } from "@/utils/zipDownloader";
import ChatMediaGallery from "../ChatMediaGallery.vue";

export default {
    name: "VisitorMediaAndDocuments",
    props: {
        visitorId: {
            type: String,
            required: true,
        },
        allowGoToChatMessage: {
            type: Boolean,
            default: false,
        },
        activeConversationId: {
            type: String,
            default: "",
        },
    },
    components: {
        ChatMediaGallery,
    },
    data() {
        return {
            activeTab: "images",
            loading: true,
            selectedImages: [],
            selectedFiles: [],
            maxSelection: 30,
            isDownloading: false,
            downloadProgress: "",
            visitorGalleryVisible: false,
            visitorGalleryItems: [],
            visitorGalleryIndex: 0,
        };
    },
    computed: {
        tabs() {
            return [
                { id: "images", name: this.$t("message.images"), icon: "fas fa-images" },
                { id: "files", name: this.$t("message.files"), icon: "fas fa-file-alt" },
                { id: "refresh", name: this.$t("message.refresh"), icon: "fas fa-sync" },
            ];
        },
        ...mapState({
            site: (state) => state.site.site,
            visitorMedia: (state) => state.visitor.visitorMedia,
        }),
        visitorGalleryShowGoToMessage() {
            return this.allowGoToChatMessage && !!this.activeConversationId;
        },
        images() {
            var self = this;
            return this.visitorMedia
                .filter(function (item) {
                    return (item.mime || "").indexOf("image/") === 0;
                })
                .map(function (item) {
                    return {
                        url: item.cdnUrl,
                        name: item.name,
                        mime: item.mime || "",
                        goToMessage: self.buildGoToMeta(item),
                    };
                });
        },
        files() {
            var self = this;
            return this.visitorMedia
                .filter(function (item) {
                    return (item.mime || "").indexOf("image/") !== 0;
                })
                .map(function (item) {
                    return {
                        url: item.cdnUrl,
                        name: item.name,
                        mime: item.mime || "",
                        size: (item.size / 1024).toFixed(2) + " KB",
                        goToMessage: self.buildGoToMeta(item),
                    };
                });
        },
        isSelectionLimitReached() {
            if (this.selectedImages.length >= this.maxSelection || this.selectedFiles.length >= this.maxSelection) {
                this.$message.error(this.$t("message.selectionLimitError"));
                return true;
            }
            return false;
        },
    },
    methods: {
        ...mapActions({
            fetchVisitorMedia: "visitor/fetchVisitorMedia",
        }),
        async loadVisitorMedia() {
            try {
                await this.fetchVisitorMedia({
                    siteId: this.site._id,
                    visitorId: String(this.visitorId),
                });
            } catch (error) {
                if (this.$message) {
                    this.$message.error(this.$t("message.errorLoadingMedia") || "Medya yüklenemedi.");
                }
            } finally {
                this.loading = false;
            }
        },
        onImageLoadError(e) {
            var img = e && e.target;
            if (!img) return;
            img.classList.add("image--broken");
        },
        handleTabClick(tabId) {
            if (tabId === "refresh") {
                this.refreshMedia();
            } else {
                this.activeTab = tabId;
            }
        },
        async refreshMedia() {
            this.loading = true;
            await this.loadVisitorMedia();
        },
        openFile(url) {
            window.open(url, "_blank");
        },
        buildGoToMeta(item) {
            if (!this.allowGoToChatMessage || !this.activeConversationId) return null;
            var cid = item.conversationId;
            if (!cid || String(cid) !== String(this.activeConversationId)) return null;
            if (!item.uniqMessageId && !item.messageId) return null;
            return {
                uniqMessageId: item.uniqMessageId || null,
                messageId: item.messageId || null,
                conversationId: item.conversationId ? String(item.conversationId) : null,
            };
        },
        handleVisitorGalleryGoToMessage(payload) {
            if (!this.allowGoToChatMessage || !this.activeConversationId) return;
            var scrollTarget = payload && payload.scrollTarget;
            if (!scrollTarget) return;
            if (String(scrollTarget.conversationId) !== String(this.activeConversationId)) {
                if (this.$message) {
                    this.$message.warning("Bu medya açık görüşmeye ait değil.");
                }
                return;
            }
            this.visitorGalleryVisible = false;
            this.$emit("visitor-go-to-chat-message", scrollTarget);
        },
        openVisitorGallery(items, index) {
            if (this.isDownloading) return;
            if (!items || !items.length) return;
            this.visitorGalleryItems = items;
            var max = Math.max(0, items.length - 1);
            this.visitorGalleryIndex = index < 0 ? 0 : index > max ? max : index;
            this.visitorGalleryVisible = true;
        },
        toggleImageSelection(url) {
            if (this.selectedImages.includes(url)) {
                this.selectedImages.splice(this.selectedImages.indexOf(url), 1);
            } else if (this.selectedImages.length < this.maxSelection) {
                this.selectedImages.push(url);
            } else {
                this.$message.error(this.$t("message.selectionLimitError"));
            }
        },
        toggleFileSelection(url) {
            if (this.selectedFiles.includes(url)) {
                this.selectedFiles.splice(this.selectedFiles.indexOf(url), 1);
            } else if (this.selectedFiles.length < this.maxSelection) {
                this.selectedFiles.push(url);
            } else {
                this.$message.error(this.$t("message.selectionLimitError"));
            }
        },
        async downloadSelectedImages() {
            if (this.selectedImages.length === 0) {
                this.$message.warning(this.$t("message.noImageSelected"));
                return;
            }

            this.isDownloading = true;
            const urls = [...this.selectedImages];

            try {
                await downloadUrlsAsZip(urls, "resimler", {
                    onProgress: (current, total) => {
                        this.downloadProgress = `İndiriliyor... (${current}/${total})`;
                    },
                    onSuccess: (successCount, failCount) => {
                        if (failCount > 0) {
                            this.$message.warning(`${successCount} dosya indirildi, ${failCount} dosya başarısız oldu.`);
                        } else {
                            this.$message.success(`${successCount} dosya başarıyla indirildi!`);
                        }
                    },
                    onError: (error) => {
                        this.$message.error(`Hata: ${error.message}`);
                    },
                });

                this.selectedImages = [];
            } catch (error) {
                // Hata zaten callback'te handle edildi
            } finally {
                this.isDownloading = false;
                this.downloadProgress = "";
            }
        },
        async downloadSelectedFiles() {
            if (this.selectedFiles.length === 0) {
                this.$message.warning(this.$t("message.noFileSelected"));
                return;
            }

            this.isDownloading = true;
            const urls = [...this.selectedFiles];

            try {
                await downloadUrlsAsZip(urls, "dosyalar", {
                    onProgress: (current, total) => {
                        this.downloadProgress = `İndiriliyor... (${current}/${total})`;
                    },
                    onSuccess: (successCount, failCount) => {
                        if (failCount > 0) {
                            this.$message.warning(`${successCount} dosya indirildi, ${failCount} dosya başarısız oldu.`);
                        } else {
                            this.$message.success(`${successCount} dosya başarıyla indirildi!`);
                        }
                    },
                    onError: (error) => {
                        this.$message.error(`Hata: ${error.message}`);
                    },
                });

                this.selectedFiles = [];
            } catch (error) {
                // Hata zaten callback'te handle edildi
            } finally {
                this.isDownloading = false;
                this.downloadProgress = "";
            }
        },
        truncateFileName(name, maxLength) {
            if (!name || name.length <= maxLength) return name;
            const extension = name.split(".").pop();
            const nameWithoutExt = name.substring(0, name.lastIndexOf("."));
            const truncated = nameWithoutExt.substring(0, maxLength - extension.length - 4);
            return `${truncated}...${extension}`;
        },
    },
    created() {
        this.loadVisitorMedia();
    },
};
</script>

<style lang="scss" scoped>
#VisitorMediaAndDocuments {
    width: 100%;
    height: 100%;
    background: transparent;
    display: flex;
    flex-direction: column;

    // Loading State
    .loading-container {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 300px;
    }

    .loading-spinner {
        width: 40px;
        height: 40px;
        border: 3px solid rgba(79, 70, 229, 0.1);
        border-left-color: #4f46e5;
        border-radius: 50%;
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }

    // Main Container
    .media-container {
        display: flex;
        flex-direction: column;
        height: 100%;
        gap: 16px;
        padding: 16px;

        @media (max-width: 768px) {
            padding: 10px;
            gap: 10px;
        }

        @media (max-width: 480px) {
            padding: 8px;
            gap: 8px;
        }
    }

    // Header Section
    .header-section {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        flex-wrap: wrap;

        @media (max-width: 768px) {
            gap: 8px;
        }

        @media (max-width: 480px) {
            gap: 6px;
        }
    }

    .tab-menu {
        display: flex;
        gap: 6px;
        background: white;
        padding: 4px;
        border-radius: 10px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        flex-wrap: wrap;

        @media (max-width: 768px) {
            gap: 3px;
            padding: 3px;
            border-radius: 7px;
        }

        @media (max-width: 480px) {
            width: 100%;
            justify-content: space-between;
            gap: 2px;
            padding: 2px;
            border-radius: 6px;
        }
    }

    .tab-btn {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 8px 16px;
        border: none;
        border-radius: 8px;
        background: transparent;
        color: #64748b;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        white-space: nowrap;

        @media (max-width: 768px) {
            padding: 5px 10px;
            font-size: 12px;
            gap: 4px;
            border-radius: 6px;
        }

        @media (max-width: 480px) {
            padding: 5px 8px;
            font-size: 11px;
            gap: 3px;
            flex: 1;
            justify-content: center;
            border-radius: 5px;

            span {
                display: none;
            }
        }

        i {
            font-size: 14px;

            @media (max-width: 768px) {
                font-size: 12px;
            }

            @media (max-width: 480px) {
                font-size: 14px;
            }
        }

        &:hover:not(.active) {
            background: #f1f5f9;
            color: #475569;
        }

        &.active {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
        }
    }

    .selection-info {
        display: flex;
        align-items: center;
        gap: 8px;

        .selection-count {
            padding: 6px 14px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 600;
            box-shadow: 0 2px 8px rgba(102, 126, 234, 0.25);

            @media (max-width: 768px) {
                padding: 4px 10px;
                font-size: 10px;
                border-radius: 16px;
            }

            @media (max-width: 480px) {
                padding: 3px 8px;
                font-size: 9px;
                border-radius: 14px;
            }
        }
    }

    // Content Wrapper
    .content-wrapper {
        flex: 1;
        overflow-y: auto;

        &::-webkit-scrollbar {
            width: 6px;
        }

        &::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 3px;
        }

        &::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 3px;

            &:hover {
                background: #94a3b8;
            }
        }
    }

    // Action Bar
    .action-bar {
        margin-bottom: 16px;
        display: flex;
        justify-content: flex-start;

        @media (max-width: 768px) {
            margin-bottom: 10px;
        }

        @media (max-width: 480px) {
            margin-bottom: 8px;
        }
    }

    .download-button {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 20px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        border: none;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
        box-shadow: 0 3px 12px rgba(102, 126, 234, 0.4);

        @media (max-width: 768px) {
            padding: 7px 14px;
            font-size: 11px;
            gap: 5px;
            border-radius: 6px;
        }

        @media (max-width: 480px) {
            padding: 6px 12px;
            font-size: 10px;
            gap: 4px;
            border-radius: 5px;
        }

        i {
            font-size: 14px;

            @media (max-width: 768px) {
                font-size: 12px;
            }

            @media (max-width: 480px) {
                font-size: 11px;
            }
        }

        &:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 5px 16px rgba(102, 126, 234, 0.5);
        }

        &:active:not(:disabled) {
            transform: translateY(0);
        }

        &:disabled {
            background: linear-gradient(135deg, #94a3b8 0%, #cbd5e1 100%);
            cursor: not-allowed;
            box-shadow: none;
        }
    }

    // Empty State
    .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 250px;
        text-align: center;
        padding: 30px 20px;

        @media (max-width: 768px) {
            min-height: 180px;
            padding: 20px 15px;
        }

        @media (max-width: 480px) {
            min-height: 150px;
            padding: 15px 10px;
        }

        .empty-icon {
            width: 64px;
            height: 64px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 16px;
            background: linear-gradient(135deg, #e0e7ff 0%, #f3e8ff 100%);
            border-radius: 50%;

            @media (max-width: 768px) {
                width: 50px;
                height: 50px;
                margin-bottom: 12px;
            }

            @media (max-width: 480px) {
                width: 42px;
                height: 42px;
                margin-bottom: 10px;
            }

            i {
                font-size: 28px;
                color: #8b5cf6;

                @media (max-width: 768px) {
                    font-size: 22px;
                }

                @media (max-width: 480px) {
                    font-size: 18px;
                }
            }
        }

        .empty-text {
            font-size: 14px;
            color: #64748b;
            font-weight: 500;

            @media (max-width: 768px) {
                font-size: 12px;
            }

            @media (max-width: 480px) {
                font-size: 11px;
            }
        }
    }

    // Image Grid
    .image-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
        gap: 12px;
        padding-bottom: 16px;

        @media (max-width: 768px) {
            grid-template-columns: repeat(auto-fill, minmax(75px, 1fr));
            gap: 8px;
            padding-bottom: 12px;
        }

        @media (max-width: 480px) {
            grid-template-columns: repeat(auto-fill, minmax(65px, 1fr));
            gap: 6px;
            padding-bottom: 10px;
        }
    }

    .image-card {
        position: relative;
        aspect-ratio: 1;
        border-radius: 10px;
        overflow: hidden;
        cursor: pointer;
        background: white;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
        transition: all 0.3s ease;

        @media (max-width: 768px) {
            border-radius: 7px;
            box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
        }

        @media (max-width: 480px) {
            border-radius: 5px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        }

        &:hover {
            transform: translateY(-3px);
            box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);

            @media (max-width: 768px) {
                transform: translateY(-2px);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
            }

            @media (max-width: 480px) {
                transform: translateY(-1px);
                box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
            }

            .image-overlay {
                opacity: 1;
            }
        }

        &.selected {
            box-shadow: 0 0 0 2px #667eea;
            transform: translateY(-2px);

            @media (max-width: 768px) {
                box-shadow: 0 0 0 1.5px #667eea;
            }

            @media (max-width: 480px) {
                box-shadow: 0 0 0 1px #667eea;
            }

            .checkbox-label::after {
                opacity: 1;
                transform: scale(1);
            }
        }

        .image {
            width: 100%;
            height: 100%;
            object-fit: cover;
            display: block;

            &.image--broken {
                object-fit: contain;
                padding: 25%;
                background: #f1f5f9;
                opacity: 0.6;
            }
        }

        .checkbox-wrapper {
            position: absolute;
            top: 6px;
            left: 6px;
            z-index: 20;

            @media (max-width: 768px) {
                top: 5px;
                left: 5px;
            }

            @media (max-width: 480px) {
                top: 4px;
                left: 4px;
            }
        }

        .custom-checkbox {
            display: none;

            &:checked + .checkbox-label {
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                border-color: #667eea;

                &::after {
                    opacity: 1;
                    transform: scale(1);
                }
            }

            &:disabled + .checkbox-label {
                opacity: 0.5;
                cursor: not-allowed;
            }
        }

        .checkbox-label {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 20px;
            height: 20px;
            background: white;
            border: 2px solid #cbd5e1;
            border-radius: 4px;
            cursor: pointer;
            transition: all 0.2s ease;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

            @media (max-width: 768px) {
                width: 18px;
                height: 18px;
                border-width: 1.5px;
                border-radius: 3px;
            }

            @media (max-width: 480px) {
                width: 16px;
                height: 16px;
                border-width: 1.5px;
                border-radius: 3px;
            }

            &::after {
                content: '\f00c';
                font-family: 'Font Awesome 5 Free';
                font-weight: 900;
                color: white;
                font-size: 10px;
                opacity: 0;
                transform: scale(0);
                transition: all 0.2s ease;

                @media (max-width: 768px) {
                    font-size: 9px;
                }

                @media (max-width: 480px) {
                    font-size: 8px;
                }
            }

            &:hover {
                border-color: #667eea;
                box-shadow: 0 3px 6px rgba(102, 126, 234, 0.3);
            }
        }

        .image-overlay {
            position: absolute;
            inset: 0;
            background: linear-gradient(to top, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.3) 50%, transparent 100%);
            opacity: 0;
            transition: opacity 0.3s ease;
            display: flex;
            align-items: flex-end;
            padding: 8px;
            pointer-events: none;

            @media (max-width: 768px) {
                padding: 6px;
            }

            @media (max-width: 480px) {
                padding: 4px;
            }
        }

        .overlay-content {
            display: flex;
            flex-direction: column;
            gap: 3px;
            width: 100%;

            @media (max-width: 480px) {
                gap: 2px;
            }
        }

        .zoom-icon {
            color: white;
            font-size: 14px;
            margin-bottom: 2px;

            @media (max-width: 768px) {
                font-size: 12px;
                display: none;
            }

            @media (max-width: 480px) {
                display: none;
            }
        }

        .image-name {
            color: white;
            font-size: 10px;
            font-weight: 500;
            line-height: 1.2;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;

            @media (max-width: 768px) {
                font-size: 9px;
            }

            @media (max-width: 480px) {
                font-size: 8px;
            }
        }
    }

    // File List
    .file-list {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding-bottom: 16px;

        @media (max-width: 768px) {
            gap: 6px;
            padding-bottom: 12px;
        }

        @media (max-width: 480px) {
            gap: 5px;
            padding-bottom: 10px;
        }
    }

    .file-card {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px;
        background: white;
        border-radius: 10px;
        transition: all 0.3s ease;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
        cursor: pointer;

        @media (max-width: 768px) {
            gap: 8px;
            padding: 8px;
            border-radius: 7px;
            box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
        }

        @media (max-width: 480px) {
            gap: 6px;
            padding: 6px;
            border-radius: 5px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        }

        &:hover {
            transform: translateX(3px);
            box-shadow: 0 3px 12px rgba(0, 0, 0, 0.12);

            @media (max-width: 768px) {
                transform: translateX(2px);
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            }

            @media (max-width: 480px) {
                transform: translateX(1px);
                box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
            }
        }

        &.selected {
            box-shadow: 0 0 0 2px #667eea;
            background: #f8f9ff;

            @media (max-width: 768px) {
                box-shadow: 0 0 0 1.5px #667eea;
            }

            @media (max-width: 480px) {
                box-shadow: 0 0 0 1px #667eea;
            }

            .checkbox-label::after {
                opacity: 1;
                transform: scale(1);
            }
        }

        .checkbox-wrapper {
            flex-shrink: 0;
        }

        .custom-checkbox {
            display: none;

            &:checked + .checkbox-label {
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                border-color: #667eea;

                &::after {
                    opacity: 1;
                    transform: scale(1);
                }
            }

            &:disabled + .checkbox-label {
                opacity: 0.5;
                cursor: not-allowed;
            }
        }

        .checkbox-label {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 18px;
            height: 18px;
            background: white;
            border: 2px solid #cbd5e1;
            border-radius: 4px;
            cursor: pointer;
            transition: all 0.2s ease;

            @media (max-width: 768px) {
                width: 16px;
                height: 16px;
                border-width: 1.5px;
                border-radius: 3px;
            }

            @media (max-width: 480px) {
                width: 14px;
                height: 14px;
                border-width: 1.5px;
                border-radius: 3px;
            }

            &::after {
                content: '\f00c';
                font-family: 'Font Awesome 5 Free';
                font-weight: 900;
                color: white;
                font-size: 9px;
                opacity: 0;
                transform: scale(0);
                transition: all 0.2s ease;

                @media (max-width: 768px) {
                    font-size: 8px;
                }

                @media (max-width: 480px) {
                    font-size: 7px;
                }
            }

            &:hover {
                border-color: #667eea;
            }
        }

        .file-icon-wrapper {
            flex-shrink: 0;
            width: 38px;
            height: 38px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, #e0e7ff 0%, #f3e8ff 100%);
            border-radius: 7px;

            @media (max-width: 768px) {
                width: 34px;
                height: 34px;
                border-radius: 6px;
            }

            @media (max-width: 480px) {
                width: 30px;
                height: 30px;
                border-radius: 5px;
            }

            .file-icon {
                font-size: 18px;
                color: #8b5cf6;

                @media (max-width: 768px) {
                    font-size: 16px;
                }

                @media (max-width: 480px) {
                    font-size: 14px;
                }
            }
        }

        .file-info {
            flex: 1;
            min-width: 0;

            .file-name {
                font-size: 12px;
                font-weight: 600;
                color: #1e293b;
                margin-bottom: 2px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;

                @media (max-width: 768px) {
                    font-size: 11px;
                }

                @media (max-width: 480px) {
                    font-size: 10px;
                }
            }

            .file-size {
                font-size: 10px;
                color: #64748b;

                @media (max-width: 768px) {
                    font-size: 9px;
                }

                @media (max-width: 480px) {
                    font-size: 8px;
                }
            }
        }

        .file-action-btn {
            flex-shrink: 0;
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f1f5f9;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.2s ease;

            @media (max-width: 768px) {
                width: 28px;
                height: 28px;
                border-radius: 5px;
            }

            @media (max-width: 480px) {
                width: 26px;
                height: 26px;
                border-radius: 4px;
            }

            i {
                font-size: 12px;
                color: #64748b;

                @media (max-width: 768px) {
                    font-size: 11px;
                }

                @media (max-width: 480px) {
                    font-size: 10px;
                }
            }

            &:hover {
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);

                i {
                    color: white;
                }
            }
        }
    }

    // Animations
    .slide-down-enter-active,
    .slide-down-leave-active {
        transition: all 0.3s ease;
    }

    .slide-down-enter,
    .slide-down-leave-to {
        opacity: 0;
        transform: translateY(-10px);
    }
}
</style>
